import { Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ServiceTemplateService as ServiceTemplateApiService } from '@netfoundry-ui/shared/apiv2';
import { ServiceTemplateV2, UpdateServiceTemplate } from '@netfoundry-ui/shared/model';
import { ApiService } from '@netfoundry-ui/shared/services';
import { ServiceTemplate } from './service-template.model';
import { ServiceTemplateInput, ServiceTemplateStorage } from './service-template-storage';

/**
 * HTTP-backed {@link ServiceTemplateStorage} for the cloud-ziti app. Persists
 * templates via the core `/core/v3/service-templates` API and maps between the
 * UI `ServiceTemplate` shape and the backend `ServiceTemplateV2` resource:
 * `name` lives at the top level; everything else UI-specific is stored in the
 * free-form `data` blob. `id`, `type` and timestamps are server-assigned.
 *
 * Only CUSTOM templates can be created/updated/deleted; GLOBAL templates are
 * read-only server-side and callers should gate write actions accordingly.
 */
@Injectable({
    providedIn: 'root',
})
export class ApiServiceTemplateStorage implements ServiceTemplateStorage {
    constructor(private api: ServiceTemplateApiService, private apiService: ApiService) {}

    async getAll(): Promise<ServiceTemplate[]> {
        const records = await this.api.getServiceTemplates();
        return records.map((record) => this.fromV2(record));
    }

    async create(template: ServiceTemplateInput): Promise<ServiceTemplate> {
        const networkGroupId = await this.currentNetworkGroupId();
        const created = await lastValueFrom(
            this.api.createServiceTemplate({
                networkGroupId,
                name: template.name,
                description: template.description ?? '',
                data: this.toData(template),
            })
        );
        return this.fromV2(created);
    }

    async update(id: string, template: ServiceTemplateInput): Promise<ServiceTemplate> {
        const body: UpdateServiceTemplate = {
            name: template.name,
            description: template.description ?? '',
            data: this.toData(template),
        };
        const updated = await lastValueFrom(this.api.updateServiceTemplate(id, body));
        return this.fromV2(updated);
    }

    async delete(id: string): Promise<void> {
        await lastValueFrom(this.api.deleteServiceTemplate(id));
    }

    /** Serialize the UI-specific fields into the backend `data` blob. `description` is a top-level column. */
    private toData(template: ServiceTemplateInput): Record<string, any> {
        return {
            serviceFields: template.serviceFields,
            configTypes: template.configTypes,
        };
    }

    /** Reconstruct the UI `ServiceTemplate` from a persisted resource. */
    private fromV2(record: ServiceTemplateV2): ServiceTemplate {
        const data = record.data ?? {};
        return {
            id: record.id,
            name: record.name,
            // description is a top-level column; fall back to the old data blob for any transition-era records.
            description: record.description ?? data.description,
            serviceFields: data.serviceFields ?? { includeRoleAttributes: false },
            configTypes: data.configTypes ?? [],
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
            type: record.type,
            networkGroupId: record.networkGroupId,
        };
    }

    private async currentNetworkGroupId(): Promise<string> {
        const networkGroup = await firstValueFrom(this.apiService.currentNetworkGroup);
        const id = networkGroup?.id;
        if (!id) {
            throw new Error('No current network group is selected; cannot create a service template.');
        }
        return id;
    }
}
